import { useMutation, useQuery, useQueryClient } from 'react-query';
import useSnackbar from '../../hooks/useSnackbar';
import { fetchLabeledThemes, updatePopularThemes, updateTheme } from '../../services/api/Theme';
import { ITheme } from '../../services/types';
import { replace } from '../../services/utils';

enum QueryKey {
  AllThemes = 'allThemes',
}

const useThemes = () => {
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();

  const { data: themes, error } = useQuery<ITheme[], Error>(QueryKey.AllThemes, async () => {
    try {
      const [fetchPromise] = fetchLabeledThemes();
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data.splice(1).reduce((acc: ITheme[], cur) => [...acc, ...cur.themes], []);
    } catch (e) {
      throw e;
    }
  });

  const themeMutation = useMutation<ITheme, Error, ITheme>(
    async (theme: ITheme) => {
      try {
        const result = await updateTheme(theme);
        return result.data;
      } catch (e) {
        throw e;
      }
    },
    {
      onSuccess: (theme: ITheme) => {
        snackbar.success('Updated successfully.');
        queryClient.setQueryData<ITheme[]>(QueryKey.AllThemes, (queryData = []) => replace<ITheme>(queryData, t => t.id === theme.id, theme));
      },
      onError: (e: Error) => snackbar.error(e.message),
    },
  );

  const popularThemeMutation = useMutation<ITheme[], Error, ITheme[]>(
    async (themes: ITheme[]) => {
      try {
        const result = await updatePopularThemes(themes);
        return result.data;
      } catch (e) {
        throw e;
      }
    },
    {
      onSuccess: () => {
        snackbar.success('Updated successfully.');
        queryClient.invalidateQueries(QueryKey.AllThemes);
      },
      onError: (e: Error) => snackbar.error(e.message),
    },
  );

  return {
    themes,
    themeMutation,
    popularThemeMutation,
    error,
  };
};

export default useThemes;
